import { palette } from '@kimuno/theme';

/**
 * Adds inline styles to table elements for PDF export.
 *
 * When HTML contains math formulas rendered by KaTeX, the conversion process
 * may inject <link> tags for KaTeX CSS. This function preserves those links
 * by returning them at the beginning of the output.
 *
 * @param html - The HTML string to process
 * @returns HTML string with inline table styles. If the input HTML contains
 *          <link> or <style> tags (e.g., from KaTeX CSS), they will be preserved
 *          at the beginning of the output for proper rendering in PDF.
 */
export function addInlineTableStyles(html: string): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');

	// Style tables
	const tables = doc.querySelectorAll('table');
	tables.forEach((table) => {
		const tableElement = table as HTMLTableElement;
		tableElement.style.borderCollapse = 'collapse';
		tableElement.style.tableLayout = 'fixed';
		tableElement.style.width = '100%';
		tableElement.style.margin = '1rem 0';
		tableElement.style.overflow = 'hidden';
	});

	// Style table cells (td and th)
	const cells = doc.querySelectorAll('td, th');
	cells.forEach((cell) => {
		const cellElement = cell as HTMLElement;
		cellElement.style.minWidth = '1em';
		cellElement.style.border = `1px solid ${palette.divider}`;
		cellElement.style.padding = '8px';
		cellElement.style.verticalAlign = 'top';
		cellElement.style.boxSizing = 'border-box';
	});

	// Style table headers
	const headers = doc.querySelectorAll('th');
	headers.forEach((th) => {
		const headerElement = th as HTMLElement;
		headerElement.style.fontWeight = 'bold';
		headerElement.style.textAlign = 'left';
		headerElement.style.backgroundColor = palette.background.paperElevation2;
	});

	// Return head content (for CSS links) + body content
	// This preserves any <link> or <style> tags that were moved to <head> by DOMParser
	const headContent = doc.head.innerHTML.trim();
	const bodyContent = doc.body.innerHTML;

	// Only include head content if it exists (e.g., KaTeX CSS link)
	return headContent ? headContent + bodyContent : bodyContent;
}
